import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, DialogActions, DialogContent, IconButton, Popper } from '@mui/material'
import { ChevronDown, X } from 'lucide-react'
import Button from '../ui/Button'
import Input from '../ui/Input'
import type { CodeItemAttributeDefinition, CodeItemGroup, CodeRow } from '../../types/codeIndex'
import {
  formatEnumeratedAttributeValues,
  getAttributeValueValidationError,
  normalizeAttributeValues,
  parseEnumeratedAttributeValues,
} from '../../utils/codeItemAttributes'

type ValueAttributesDialogProps = {
  open: boolean
  group: CodeItemGroup | null
  row: CodeRow | null
  attributes: CodeItemAttributeDefinition[]
  initialFocusAttributeId?: string | null
  onClose: () => void
  onSave: (values: Record<string, string | null>) => void
}

const dropdownPopperModifiers = [
  {
    name: 'offset',
    options: {
      offset: [0, 6],
    },
  },
  {
    name: 'preventOverflow',
    options: {
      padding: 12,
    },
  },
  {
    name: 'flip',
    options: {
      padding: 12,
    },
  },
]

type EnumeratedMultiSelectProps = {
  label: string
  values: string[]
  options: string[]
  invalid?: boolean
  onChange: (values: string[]) => void
}

const EnumeratedMultiSelect = React.forwardRef<HTMLButtonElement, EnumeratedMultiSelectProps>(({
  label,
  values,
  options,
  invalid = false,
  onChange,
}, forwardedRef) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const mergedOptions = useMemo(() => {
    const missingValues = values.filter(value => !options.includes(value))
    return [...options, ...missingValues]
  }, [options, values])
  const displayText = values.length > 0
    ? values.join(', ')
    : options.length > 0
      ? 'Select values'
      : 'No options available'

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        (!menuRef.current || !menuRef.current.contains(target))
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const setButtonRef = (node: HTMLButtonElement | null) => {
    if (typeof forwardedRef === 'function') {
      forwardedRef(node)
    } else if (forwardedRef) {
      forwardedRef.current = node
    }
  }

  const toggleValue = (value: string) => {
    if (values.includes(value)) {
      onChange(values.filter(item => item !== value))
      return
    }

    onChange([...values, value])
  }

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        ref={setButtonRef}
        type="button"
        onClick={() => setIsOpen(current => !current)}
        className={`w-full flex items-center justify-between gap-1 border rounded bg-white px-3 py-2 text-sm ${
          isOpen
            ? 'border-[#1D74D8] ring-1 ring-[#1D74D8]'
            : invalid
              ? 'border-[#B3261E]'
              : 'border-[#CBD5E1] hover:border-[#1D74D8]'
        }`}
        aria-label={label}
      >
        <span className={`min-w-0 flex-1 truncate text-left ${values.length > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
          {displayText}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <Popper
        open={isOpen}
        anchorEl={containerRef.current}
        placement="bottom-start"
        modifiers={dropdownPopperModifiers}
        sx={{ zIndex: 1600, width: containerRef.current?.offsetWidth }}
      >
        <div ref={menuRef} className="max-h-60 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="max-h-48 overflow-y-auto">
            {mergedOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No options available</div>
            ) : (
              mergedOptions.map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleValue(option)}
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                    values.includes(option) ? 'bg-blue-50 text-[#1D74D8]' : 'text-gray-700'
                  }`}
                >
                  <span className="truncate">{option}</span>
                  <input
                    type="checkbox"
                    readOnly
                    checked={values.includes(option)}
                    className="h-4 w-4 shrink-0 pointer-events-none"
                  />
                </button>
              ))
            )}
          </div>
        </div>
      </Popper>
    </div>
  )
})

EnumeratedMultiSelect.displayName = 'EnumeratedMultiSelect'

export const ValueAttributesDialog: React.FC<ValueAttributesDialogProps> = ({
  open,
  group,
  row,
  attributes,
  initialFocusAttributeId = null,
  onClose,
  onSave,
}) => {
  const [draftValues, setDraftValues] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const fieldRefs = useRef<Record<string, HTMLInputElement | HTMLButtonElement | null>>({})

  useEffect(() => {
    if (!open) return
    const normalizedValues = normalizeAttributeValues(row?.attributeValues)
    setDraftValues(attributes.reduce<Record<string, string>>((accumulator, attribute) => {
      accumulator[attribute.id] = String(normalizedValues[attribute.id] || '')
      return accumulator
    }, {}))
    setErrors({})
  }, [attributes, open, row])

  useEffect(() => {
    if (!open || !initialFocusAttributeId) return

    const focusTimer = window.setTimeout(() => {
      const field = fieldRefs.current[initialFocusAttributeId]
      if (!field) return

      field.focus()
      if (field instanceof HTMLInputElement) {
        field.select()
      }
    }, 0)

    return () => window.clearTimeout(focusTimer)
  }, [attributes, initialFocusAttributeId, open])

  const contextText = useMemo(() => {
    if (!group || !row) return ''
    const optionCode = row.optionCode || '(blank)'
    const description = row.description || ''
    return `Code Item ${group.codeItemPos.code} - Value ${optionCode}${description ? ` - ${description}` : ''}`
  }, [group, row])

  const handleSave = () => {
    const nextErrors = attributes.reduce<Record<string, string>>((accumulator, attribute) => {
      const error = getAttributeValueValidationError(attribute, draftValues[attribute.id])
      if (error) {
        accumulator[attribute.id] = error
      }
      return accumulator
    }, {})

    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    onSave(attributes.reduce<Record<string, string | null>>((accumulator, attribute) => {
      const value = String(draftValues[attribute.id] || '').trim()
      accumulator[attribute.id] = value || null
      return accumulator
    }, {}))
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '8px' } }}
    >
      <div className="flex items-start justify-between border-b border-[#E2E8F0] px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Edit value attributes</h2>
          <p className="text-xs text-gray-500">{contextText}</p>
        </div>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </IconButton>
      </div>

      <DialogContent sx={{ padding: '16px 20px 8px' }}>
        <div className="space-y-3">
          {attributes.map(attribute => {
            const error = errors[attribute.id] || null
            const unit = String(attribute.unit || '').trim()
            return (
              <div key={attribute.id} className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm font-medium text-gray-800">{attribute.name}</label>
                  <span className="text-xs text-gray-500">
                    {attribute.type}{unit ? ` - ${unit}` : ''}
                  </span>
                </div>
                {attribute.type === 'enumerated' ? (
                  <>
                    <EnumeratedMultiSelect
                      ref={(node) => {
                        fieldRefs.current[attribute.id] = node
                      }}
                      label={attribute.name}
                      values={parseEnumeratedAttributeValues(draftValues[attribute.id])}
                      options={attribute.allowedValues || []}
                      invalid={Boolean(error)}
                      onChange={(selectedValues) => {
                        setDraftValues(current => ({
                          ...current,
                          [attribute.id]: formatEnumeratedAttributeValues(selectedValues, attribute.allowedValues || []),
                        }))
                      }}
                    />
                    {parseEnumeratedAttributeValues(draftValues[attribute.id]).length > 0 && (
                      <button
                        type="button"
                        className="mt-2 text-xs font-medium text-[#1D5176] hover:underline"
                        onClick={() => setDraftValues(current => ({ ...current, [attribute.id]: '' }))}
                      >
                        Clear selection
                      </button>
                    )}
                  </>
                ) : (
                  <Input
                    ref={(node) => {
                      fieldRefs.current[attribute.id] = node
                    }}
                    type={attribute.type === 'numeric' ? 'number' : 'text'}
                    step={attribute.type === 'numeric' ? 'any' : undefined}
                    value={draftValues[attribute.id] || ''}
                    onChange={(event) => setDraftValues(current => ({ ...current, [attribute.id]: event.target.value }))}
                    placeholder="Leave blank if not applicable"
                    inputMode={attribute.type === 'numeric' ? 'decimal' : undefined}
                    inputSize="md"
                    invalid={Boolean(error)}
                    aria-label={attribute.name}
                  />
                )}
                {error && <div className="text-xs text-[#B3261E]" role="alert">{error}</div>}
              </div>
            )
          })}
        </div>
      </DialogContent>

      <DialogActions sx={{ padding: '12px 20px 16px' }}>
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" variant="primary" size="sm" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ValueAttributesDialog
